import React from "react";

import { AddressElementProps } from "@stripe/react-stripe-js";
import { compact, isEmpty } from "lodash/fp";

import { Typography } from "@mui/material";

import {
  BillingDetailsLoading,
  CompletedPaymentInfo,
  EditFormButton,
  StripeElementContainer,
} from "@app/analytics/account/Billing/checkout/styles";
import type { CheckoutBillingInfo } from "@app/common/types";

import Stack from "@rui/Stack";

import AddressForm from "./AddressForm";

interface AddressModuleProps {
  loading?: boolean;
  saving?: boolean;
  open?: boolean;
  onEdit: (event: React.MouseEvent) => void;
  onSave: (checkoutBillingInfo: CheckoutBillingInfo) => void;
  onLoadError: AddressElementProps["onLoadError"];
  checkoutBillingInfo: CheckoutBillingInfo | null;
}

const AddressModule = ({
  loading,
  saving,
  open,
  onEdit,
  onSave,
  onLoadError,
  checkoutBillingInfo,
}: AddressModuleProps) => {
  if (loading || !checkoutBillingInfo) {
    return <BillingDetailsLoading />;
  }

  if (open) {
    return (
      <StripeElementContainer in={open} unmountOnExit>
        <AddressForm
          onSave={onSave}
          onLoadError={onLoadError}
          checkoutBillingInfo={checkoutBillingInfo}
          loading={saving}
        />
      </StripeElementContainer>
    );
  }

  const address = checkoutBillingInfo.address;
  const cityStateZip = compact([
    address?.city,
    compact([address?.state, address?.postalCode]).join(" "),
  ]).join(", ");

  return (
    <CompletedPaymentInfo>
      <Stack>
        {checkoutBillingInfo.company && (
          <Typography variant="bodySecondaryReg">
            {checkoutBillingInfo.company}
          </Typography>
        )}
        <Typography variant="bodySecondaryReg">
          {checkoutBillingInfo.name}
        </Typography>

        {address?.line1 && (
          <Typography variant="bodySecondaryReg">{address.line1}</Typography>
        )}
        {address?.line2 && (
          <Typography variant="bodySecondaryReg">{address.line2}</Typography>
        )}
        {!isEmpty(cityStateZip) && (
          <Typography variant="bodySecondaryReg">{cityStateZip}</Typography>
        )}
        {address?.country && (
          <Typography variant="bodySecondaryReg">{address?.country}</Typography>
        )}
      </Stack>
      <EditFormButton onClick={onEdit}>Edit</EditFormButton>
    </CompletedPaymentInfo>
  );
};

export default AddressModule;
